import { Component, computed, EventEmitter, inject, Input, Output, signal, OnInit } from '@angular/core';
import { DatetimeCustomEvent } from '@ionic/angular';
import { ModalController } from '@ionic/angular/lazy';
import { IonicModule } from '@ionic/angular/lazy';
import { TranslateModule } from '@ngx-translate/core';

export type TimeRange = {
    start: string;
    end: string;
    touched?: {
        start: boolean;
        end: boolean;
    };
};

@Component({
    selector: 'web-modal-time-range-picker-component',
    imports: [IonicModule, TranslateModule],
    templateUrl: './modal-time-range-picker.component.html',
    styleUrl: './modal-time-range-picker.component.scss',
})
export class ModalTimeRangePickerComponentComponent implements OnInit {
    private readonly modalCtrl = inject(ModalController);

    @Input() field = '';
    @Input() items: TimeRange[] = [];
    @Input() title = '';
    @Input() type: 'create' | 'edit' = 'create';
    @Input() initialValue?: TimeRange;
    @Input() editIndex?: number;

    @Output() timeRangeConfirm = new EventEmitter<TimeRange>();

    DEFAULT_TIME_RANGE: TimeRange = {
        start: '00:00',
        end: '00:00',
        touched: {
            start: false,
            end: false,
        },
    };

    timeRange = signal<TimeRange>(this.DEFAULT_TIME_RANGE);

    isConfirmDisabled = computed(() => {
        const touched = this.timeRange().touched;
        return !touched || !touched.start || !touched.end;
    });

    startDateTimeValue = computed(() => {
        const time = this.timeRange().start;
        return this.convertTimeToDateTime(time);
    });

    endDateTimeValue = computed(() => {
        const time = this.timeRange().end;
        return this.convertTimeToDateTime(time);
    });

    private convertTimeToDateTime(time: string): string {
        const [hours, minutes] = time.split(':');
        const today = new Date();
        today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        return today.toISOString();
    }

    ngOnInit() {
        if (this.initialValue) {
            this.timeRange.set({
                start: this.initialValue.start,
                end: this.initialValue.end,
                touched: {
                    start: true,
                    end: true,
                },
            });
        }
    }

    cancelChanges() {
        this.modalCtrl.dismiss();
        this.resetTimeRange();
    }

    confirmChanges() {
        const { touched, ...timeRangeData } = this.timeRange();
        this.timeRangeConfirm.emit(timeRangeData);
        this.modalCtrl.dismiss({ timeRange: timeRangeData });
        this.resetTimeRange();
    }

    resetTimeRange() {
        this.timeRange.set(this.DEFAULT_TIME_RANGE);
    }

    onTimeChange(type: keyof Omit<TimeRange, 'touched'>, event: DatetimeCustomEvent) {
        const { value } = event.detail;
        if (!value) return;
        if (typeof value !== 'string') return;

        // Split date and time
        let time = value;
        if (time.includes('T')) {
            time = time.split('T')[1];
        }
        const [hours, minutes] = time.split(':');

        const currentTimeRange = this.timeRange();
        const currentTouched = currentTimeRange.touched || { start: false, end: false };

        this.timeRange.set({
            ...currentTimeRange,
            [type]: `${hours}:${minutes}`,
            touched: {
                ...currentTouched,
                [type]: true,
            },
        });
    }
}
